'use client'

import Link from 'next/link'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { ArrowRight, Camera, Palette } from 'lucide-react'

const bands = [
  {
    id: 'videography',
    title: 'VIDEOGRAPHY',
    tagline: 'Capture Every Frame. Tell Every Story.',
    services: ['Video Shoots', 'Events', 'Drone', 'Photography', 'Post Production'],
    href: '/services/videography',
    cta: 'Explore Videography',
    icon: Camera,
    color: 'from-[#0F0D00]',
  },
  {
    id: 'graphic-design',
    title: 'GRAPHIC DESIGN',
    tagline: 'Design That Communicates. Visuals That Convert.',
    services: ['Brand Identity', 'EDMS', 'Print', 'Digital', 'Presentations'],
    href: '/services/graphic-design',
    cta: 'Explore Graphic Design',
    icon: Palette,
    color: 'from-[#000D20]',
  },
]

export default function OtherServices() {
  return (
    <section className="bg-[#0F172A]">
      {bands.map((band, i) => {
        const Icon = band.icon
        return (
          <motion.div
            key={band.id}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className={`shimmer-line relative group overflow-hidden py-20 lg:py-28 border-t border-white/10`}
            style={{
              background: `linear-gradient(135deg, ${band.color.replace('from-', '')} 0%, #0F172A 60%)`,
            }}
          >
            {/* Gold shimmer overlay on hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(200,169,110,0.04) 50%, transparent 100%)',
              }}
            />

            <div className="max-w-[1400px] mx-auto px-6 relative z-10">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
                {/* Left content */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Icon size={18} className="text-[#C8A96E]" strokeWidth={1.5} />
                    <span
                      className="text-[#C8A96E] text-xs tracking-[0.3em]"
                      style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
                    >
                      SERVICE DOMAIN {String(i + 2).padStart(2, '0')}
                    </span>
                  </div>

                  <h2
                    className="text-[clamp(3rem,7vw,6rem)] leading-none text-[#F8FAFC] mb-4"
                    style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.02em' }}
                  >
                    {band.title}
                  </h2>

                   <p className="text-[#94A3B8] text-lg italic mb-6 max-w-md">
                    {band.tagline}
                  </p>

                  {/* Service pills */}
                  <div className="flex flex-wrap gap-2 mb-8">
                    {band.services.map((s) => (
                      <span
                        key={s}
                        className="px-3 py-1 border border-white/10 text-[#94A3B8] text-xs tracking-[0.1em]"
                        style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
                      >
                        {s.toUpperCase()}
                      </span>
                    ))}
                  </div>

                  {/* Visual Preview Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl">
                    {band.id === 'videography' ? (
                      [
                        '/portfolio/photography/IMG_9977.JPEG',
                        '/portfolio/photography/IMG_9978.JPEG',
                        '/portfolio/photography/IMG_9979.JPEG',
                        '/portfolio/photography/IMG_9980.JPEG',
                      ].map((img, idx) => (
                        <div key={idx} className="aspect-video relative overflow-hidden border border-white/10 bg-[#1E293B]">
                          <Image src={img} alt="Videography preview" fill sizes="(max-width: 768px) 50vw, 25vw" className="object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-700" />
                        </div>
                      ))
                    ) : (
                      [
                        '/portfolio/graphics/raheja/WhatsApp Image 2026-03-21 at 11.14.02 AM (1).jpeg',
                        '/portfolio/graphics/raheja/WhatsApp Image 2026-03-21 at 11.14.03 AM.jpeg',
                        '/portfolio/graphics/raheja/WhatsApp Image 2026-03-21 at 11.18.25 AM (1).jpeg',
                        '/portfolio/graphics/raheja/WhatsApp Image 2026-03-21 at 11.25.54 AM.jpeg',
                      ].map((img, idx) => (
                        <div key={idx} className="aspect-video relative overflow-hidden border border-white/10 bg-[#1E293B]">
                          <Image src={img} alt="Graphic design preview" fill sizes="(max-width: 768px) 50vw, 25vw" className="object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-700" />
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Right: CTA */}
                <Link
                  href={band.href}
                  className="group/btn flex items-center gap-3 border border-[#C8A96E]/30 px-8 py-5 text-[#C8A96E] hover:bg-[#C8A96E] hover:text-[#0F172A] transition-all duration-300 flex-shrink-0"
                  style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
                >
                  <span className="text-sm tracking-[0.15em]">{band.cta.toUpperCase()}</span>
                  <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </motion.div>
        )
      })}
    </section>
  )
}
